'use client';
import React, { useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import config from '../../config';
import { useLanguage } from '../../contexts/LanguageContext';
import { scrollToTop } from '../../utils/scrollUtils';
import { ShowcaseCard, normalizeArticleProduct } from './HeroShowcase';
import {
  GUTTER, PANEL_GAP, SURFACE, RED_SHADOW, DotGrid, RedSurfaceDecor, Eyebrow, PillButton,
} from './landingUi';
import { setClickSource } from '../../services/analytics/analyticsService';

/** Hex + 2-char alpha. Returns null if hex isn't a clean 6-digit color. */
const withAlpha = (hex, aa) => {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  return `${hex}${aa}`;
};

const RADIUS = 'rounded-[2rem] lg:rounded-[2.5rem]';

/**
 * One campaign, as a bento pair:
 *   - the campaign card, solid in its accent colour (or its banner photo under
 *     an accent wash), with the name, the countdown and the CTA;
 *   - its products in a snap-scrolling rail on the off-white surface, using the
 *     hero's product card so a campaign looks like part of the same shop.
 *
 * Side by side (4/8) on desktop, stacked on phones and tablets, where the rail
 * is swiped rather than paged with arrows.
 */
const CampaignSection = React.memo(({ campaign, products }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const railRef = useRef(null);

  const accent = campaign?.accentColor || 'rgb(var(--brand))';
  const bannerSrc = campaign?.bannerImage ? `${config.imageBaseURL}${campaign.bannerImage}` : null;

  const endsAt = campaign?.endsAt ? new Date(campaign.endsAt) : null;
  const daysUntilEnd = endsAt
    ? Math.max(0, Math.ceil((endsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;
  const endsLabel = daysUntilEnd == null || daysUntilEnd > 14
    ? null
    : daysUntilEnd === 0
      ? t('home.discount.endsToday')
      : daysUntilEnd === 1
        ? t('home.discount.endsTomorrow')
        : `${t('home.discount.endsIn')} ${daysUntilEnd} ${t('home.discount.days')}`;

  const items = useMemo(
    () => (products || []).map((p) => normalizeArticleProduct(p)).filter((p) => p && p.pharmacode),
    [products]
  );

  if (!items.length) return null;

  const scrollRail = (dir) => {
    const el = railRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  const openAll = () => {
    navigate(`/products?campaign=${campaign.slug}`);
    setTimeout(() => scrollToTop(), 100);
  };

  return (
    <section className={`${GUTTER} ${PANEL_GAP}`} aria-label={`Campaign: ${campaign.name}`}>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-12 lg:gap-[clamp(0.75rem,1.2vw,1.25rem)]">
        {/* ---- The campaign card ---- */}
        <motion.div
          className={`relative flex flex-col justify-between overflow-hidden p-[clamp(1.5rem,5vw,3rem)] text-white lg:col-span-4 lg:min-h-[30rem] lg:p-[clamp(1.75rem,2.6vw,3rem)] ${RADIUS} ${RED_SHADOW}`}
          style={{ backgroundColor: accent }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          {bannerSrc && (
            <div aria-hidden="true" className="pointer-events-none absolute inset-0">
              <img src={bannerSrc} alt="" className="h-full w-full object-cover" loading="lazy" decoding="async" />
              <div
                className="absolute inset-0"
                style={{ background: `linear-gradient(180deg, ${withAlpha(accent, 'B3') || accent} 0%, ${withAlpha(accent, 'E6') || accent} 55%, ${accent} 100%)` }}
              />
            </div>
          )}
          <RedSurfaceDecor radius={RADIUS} />

          <div className="relative flex flex-wrap items-center gap-2">
            <Eyebrow tone="onRed" pulse>{t('home.discount.campaignTag') || 'Campaign'}</Eyebrow>
            {endsLabel && (
              <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: accent }}>
                {endsLabel}
              </span>
            )}
          </div>

          <div className="relative mt-6 lg:mt-10">
            <h2 className="text-[clamp(2rem,8vw,3.75rem)] font-extrabold leading-[0.98] tracking-[-0.045em] lg:text-[clamp(2.25rem,3.4vw,3.75rem)]">
              {campaign.name}
            </h2>
            {campaign.description && (
              <p className="mt-3 line-clamp-3 max-w-md text-[14px] leading-relaxed text-white/80 lg:mt-4 lg:line-clamp-4 lg:text-[15px]">
                {campaign.description}
              </p>
            )}
            <PillButton
              variant="white"
              onClick={openAll}
              className="mt-6 w-full justify-center sm:w-auto lg:mt-7"
              aria-label={`View all ${campaign.name}`}
            >
              {t('home.discount.viewAllCampaign') || 'View all'}
            </PillButton>
          </div>
        </motion.div>

        {/* ---- The product rail ---- */}
        <motion.div
          className={`relative flex min-w-0 flex-col overflow-hidden py-[clamp(1rem,4vw,2rem)] ring-1 ring-black/[0.04] lg:col-span-8 lg:p-[clamp(1.25rem,2vw,2rem)] ${RADIUS}`}
          style={{ backgroundColor: SURFACE }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.55, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
        >
          <DotGrid radius={RADIUS} />
          <div className="relative mb-3 flex items-center justify-between gap-3 px-5 lg:mb-4 lg:px-1">
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-400">
              {items.length} × {t('home.discount.dealsTag') || 'Offers'}
            </span>
            {/* Arrows only where there is a pointer; phones swipe. */}
            <div className="hidden gap-2 lg:flex">
              {[[-1, HiChevronLeft, 'Previous'], [1, HiChevronRight, 'Next']].map(([dir, Icon, label]) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => scrollRail(dir)}
                  aria-label={label}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-gray-700 ring-1 ring-black/[0.07] transition-all duration-200 hover:bg-brand hover:text-white hover:ring-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                >
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </button>
              ))}
            </div>
          </div>

          <div
            ref={railRef}
            className="campaign-rail relative flex flex-1 snap-x snap-mandatory scroll-px-5 gap-3 overflow-x-auto px-5 pb-2 pt-1 lg:-mx-1 lg:scroll-px-1 lg:gap-[clamp(0.6rem,1vw,1rem)] lg:px-1"
          >
            <style>{`.campaign-rail { scrollbar-width: none; -webkit-overflow-scrolling: touch; } .campaign-rail::-webkit-scrollbar { display: none; }`}</style>
            {items.map((item) => (
              <div key={item.id} className="h-[clamp(13.5rem,56vw,18rem)] flex-shrink-0 snap-start lg:h-[22rem]">
                <ShowcaseCard
                  item={item}
                  variant={item.price.inDiscount ? 'deal' : 'bestseller'}
                  accent={accent}
                  onSelect={(code) => { setClickSource('campaign'); navigate(`/product/${code}`); }}
                />
              </div>
            ))}
            <div className="w-px flex-shrink-0 lg:hidden" aria-hidden="true" />
          </div>
        </motion.div>
      </div>
    </section>
  );
});

CampaignSection.displayName = 'CampaignSection';

export default CampaignSection;
